/**
 * Authentication hook
 * 
 * Manages user authentication state and provides auth-related methods
 * Uses a singleton pattern to prevent multiple auth checks across components
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { User, AuthResponse } from '../types';
import { apiRequest } from '../services/api';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Shared state across all useAuth instances (singleton pattern)
let globalAuthState: {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  checkPromise: Promise<void> | null;
} = {
  user: null,
  isAuthenticated: false,
  loading: true,
  checkPromise: null,
};

// Subscription pattern: listeners for global state changes
type StateChangeListener = () => void;
let globalStateListeners: Set<StateChangeListener> = new Set();

/**
 * Notify all subscribed components of global state changes
 */
const notifyListeners = () => {
  globalStateListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.warn('[useAuth] Error in state change listener:', error);
    }
  });
};

// Cache keys
const AUTH_CACHE_KEY = 'papertrader_auth_user';
const AUTH_TIMESTAMP_KEY = 'papertrader_auth_timestamp';
const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook for authentication state management
 */
export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(globalAuthState.user);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(globalAuthState.isAuthenticated);
  const [loading, setLoading] = useState<boolean>(globalAuthState.loading);
  const isMountedRef = useRef(true);

  /**
   * Verify auth in background without blocking UI
   */
  const verifyAuthInBackground = useCallback(async () => {
    try {
      const response = await apiRequest<AuthResponse>('/account/auth');
      if (response.ok) {
        const data = await response.json() as AuthResponse;
        if (!data.success) {
          // Auth expired, clear cache
          globalAuthState.isAuthenticated = false;
          globalAuthState.user = null;
          localStorage.removeItem(AUTH_CACHE_KEY);
          localStorage.removeItem(AUTH_TIMESTAMP_KEY);
          
          // Notify all listeners of state change
          notifyListeners();
        }
      }
    } catch (error) {
      // Silently fail in background check
      console.warn('[useAuth] Background auth check failed:', error);
    }
  }, []);

  /**
   * Check authentication status with the backend
   * Uses singleton pattern to prevent multiple simultaneous requests
   */
  const checkAuth = useCallback(async () => {
    // If there's already a check in progress, wait for it
    if (globalAuthState.checkPromise) {
      await globalAuthState.checkPromise;
      if (isMountedRef.current) {
        setUser(globalAuthState.user);
        setIsAuthenticated(globalAuthState.isAuthenticated);
        setLoading(globalAuthState.loading);
      }
      return;
    }

    // Check cache first
    try {
      const cachedUser = localStorage.getItem(AUTH_CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);
      
      if (cachedUser && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        
        // Use cached data if less than 5 minutes old
        if (now - timestamp < AUTH_CACHE_DURATION) {
          const userData = JSON.parse(cachedUser) as User;
          globalAuthState.user = userData;
          globalAuthState.isAuthenticated = true;
          globalAuthState.loading = false;
          
          // Notify all listeners of state change
          notifyListeners();
          
          // Still verify auth in background (non-blocking)
          verifyAuthInBackground();
          return;
        }
      }
    } catch (error) {
      console.warn('[useAuth] Error reading cache:', error);
    }

    // Create a promise for the auth check
    globalAuthState.checkPromise = (async () => {
      try {
        const response = await apiRequest<AuthResponse>('/account/auth');
        
        if (response.ok) {
          const data = await response.json() as AuthResponse;
          if (data.success) {
            // Fetch user profile only if not cached or cache expired
            const profileResponse = await apiRequest<User>('/account/profile');
            if (profileResponse.ok) {
              const userData = await profileResponse.json() as User;
              
              // Update global state
              globalAuthState.user = userData;
              globalAuthState.isAuthenticated = true;
              
              // Cache user data
              try {
                localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
                localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
              } catch (error) {
                console.warn('[useAuth] Error caching user data:', error);
              }
              
              // Notify all listeners of state change
              notifyListeners();
            }
          } else {
            globalAuthState.isAuthenticated = false;
            globalAuthState.user = null;
            localStorage.removeItem(AUTH_CACHE_KEY);
            localStorage.removeItem(AUTH_TIMESTAMP_KEY);
            
            // Notify all listeners of state change
            notifyListeners();
          }
        } else {
          globalAuthState.isAuthenticated = false;
          globalAuthState.user = null;
          localStorage.removeItem(AUTH_CACHE_KEY);
          localStorage.removeItem(AUTH_TIMESTAMP_KEY);
          
          // Notify all listeners of state change
          notifyListeners();
        }
      } catch (error) {
        console.error('[useAuth] Auth check failed:', error);
        globalAuthState.isAuthenticated = false;
        globalAuthState.user = null;
        
        // Notify all listeners of state change
        notifyListeners();
      } finally {
        globalAuthState.loading = false;
        // Null out the promise BEFORE notifying. notifyListeners() can trigger
        // synchronous re-renders in subscribed components, and any of them may
        // call checkAuth() during that render. If checkPromise still pointed
        // at this (about-to-resolve) promise, that re-entrant caller would
        // await an already-settled promise instead of starting a fresh check —
        // benign today, but a foot-gun once any caller relies on the promise
        // representing an *in-flight* request.
        globalAuthState.checkPromise = null;

        // Notify all listeners of loading state change
        notifyListeners();
      }
    })();

    await globalAuthState.checkPromise;
  }, [verifyAuthInBackground]);

  /**
   * Sync local state with global state
   */
  const syncWithGlobalState = useCallback(() => {
    if (isMountedRef.current) {
      setUser(globalAuthState.user);
      setIsAuthenticated(globalAuthState.isAuthenticated);
      setLoading(globalAuthState.loading);
    }
  }, []);

  /**
   * Subscribe to global state changes - runs once on mount
   */
  useEffect(() => {
    // Subscribe to global state changes
    globalStateListeners.add(syncWithGlobalState);

    // Sync with global state immediately on mount
    syncWithGlobalState();

    return () => {
      isMountedRef.current = false;
      // Unsubscribe when component unmounts
      globalStateListeners.delete(syncWithGlobalState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount/unmount

  /**
   * Initialize auth check - runs once globally
   */
  useEffect(() => {
    // Only check auth if not already checked and no user exists
    if (globalAuthState.checkPromise === null && !globalAuthState.user) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  /**
   * Login handler - sets user and authenticated state
   */
  const login = useCallback((userData: User) => {
    globalAuthState.user = userData;
    globalAuthState.isAuthenticated = true;
    globalAuthState.loading = false;
    
    // Cache user data
    try {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
      localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.warn('[useAuth] Error caching user data:', error);
    }
    
    // Notify all listeners of state change
    notifyListeners();
  }, []);

  /**
   * Logout handler - clears auth state and calls backend
   */
  const logout = useCallback(async () => {
    try {
      await apiRequest('/account/logout', { method: 'POST' });
    } catch (error) {
      console.error('[useAuth] Logout failed', error);
    } finally {
      localStorage.removeItem(AUTH_CACHE_KEY);
      localStorage.removeItem(AUTH_TIMESTAMP_KEY);
      
      globalAuthState.user = null;
      globalAuthState.isAuthenticated = false;
      globalAuthState.loading = false;
      
      // Notify all listeners of state change
      notifyListeners();
    }
  }, []);

  /**
   * Force refresh user profile from server, bypassing cache
   * Useful after email verification or profile updates
   */
  const refreshUser = useCallback(async () => {
    try {
      // Clear cache to force fresh fetch
      localStorage.removeItem(AUTH_CACHE_KEY);
      localStorage.removeItem(AUTH_TIMESTAMP_KEY);
      
      // Check if authenticated first
      const authResponse = await apiRequest<AuthResponse>('/account/auth');
      if (authResponse.ok) {
        const authData = await authResponse.json() as AuthResponse;
        if (authData.success) {
          // Fetch fresh user profile
          const profileResponse = await apiRequest<User>('/account/profile');
          if (profileResponse.ok) {
            const userData = await profileResponse.json() as User;
            
            // Update global state
            globalAuthState.user = userData;
            globalAuthState.isAuthenticated = true;
            
            // Update cache with fresh data
            try {
              localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
              localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
            } catch (error) {
              console.warn('[useAuth] Error caching user data:', error);
            }
            
            // Notify all listeners of state change
            // This ensures Dashboard and all other components update immediately
            notifyListeners();
          }
        }
      }
    } catch (error) {
      console.error('[useAuth] Refresh user failed:', error);
    }
  }, []);

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth,
    refreshUser,
  };
};

